import React, { useEffect } from 'react';
import { 
  CheckCircle, 
  Zap, 
  Users, 
  Download, 
  Code, 
  FileSpreadsheet, 
  ArrowRight,
  X,
  LinkedinIcon
} from "lucide-react";
import { FaXTwitter } from "react-icons/fa6";

// JSON-LD Component
function JsonLd({ type, data }: { type: string; data: any }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': type,
      ...data,
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [type, data]);

  return null;
}

// Button Component
const Button = ({ children, className, ...props }: any) => (
  <button 
    className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 ${className}`}
    {...props}
  >
    {children}
  </button>
);

// Header Component
const Header = () => (
  <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
    <div className="flex justify-between mx-auto max-w-7xl px-10 h-20">
      <a href="/" className="flex items-center gap-3">
        <div className="w-10 h-10 relative">
          <img src="/bsk-icon-v2.png" alt="Basilisk Logo" className="w-full h-full object-contain" />
        </div>
        <span className="text-2xl font-bold text-black">Basilisk</span>
      </a>
      <nav className="flex items-center gap-6">
        <a href="/login">
          <Button className="bg-white border-2 border-green-600 text-green-600 hover:bg-green-50 font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-lg">
            Login
          </Button>
        </a>
        <a href="https://calendar.app.google/k8AR5Ya6LqkLWBJL7" target="_blank" rel="noopener noreferrer">
          <Button className="bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 text-white font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-lg">
            Schedule a Meeting
          </Button>
        </a>
      </nav>
    </div>
  </header>
);

// Hero Section Component
const HeroSection = () => (
  <section className="relative min-h-[80vh] flex items-center justify-center p-8 bg-white overflow-hidden">
    <div className="absolute inset-0 opacity-5">
      <img src="/bsk-icon-v2.png" alt="Basilisk Logo Background" className="w-full h-full object-cover" />
    </div>
    
    <div className="container mx-auto max-w-6xl relative z-10">
      <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
        <div className="text-center lg:text-left lg:max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 mb-6">
            <Zap className="h-5 w-5 text-green-600" />
            <p className="text-sm font-medium text-green-600">The new standard for game content management</p>
          </div>
          
          <h1 className="text-6xl lg:text-7xl mb-6 bg-gradient-to-r from-black to-green-600 bg-clip-text text-transparent font-bold">
            BASILISK
          </h1>
          
          <h2 className="text-2xl lg:text-3xl mb-6 text-gray-700">
            Cloud-based Content Management Platform for LiveOps content creation
          </h2>
          
          <div className="flex items-center justify-center lg:justify-start gap-2 mb-8">
            <Users className="h-6 w-6 text-green-600" />
            <span className="text-lg text-gray-700">
              Empowers Non-Technical Devs to deploy game data updates in seconds to any environment
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-8 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-600">Zero downtime</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-600">Error safeguarded</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-600">Lightning-fast</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-gray-600">Works on any tech stack</span>
            </div>
          </div>
        </div>
        
        <div className="relative h-[400px] w-[350px] lg:h-[500px] lg:w-[450px]">
          <div className="absolute inset-0 bg-gradient-to-br from-green-200 to-green-100 rounded-3xl"></div>
          <div className="relative h-full w-full flex items-center justify-center">
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-4 bg-green-600 rounded-full flex items-center justify-center p-4">
                <img src="/bsk-icon-v2.png" alt="Basilisk Logo" width={80} height={80} className="object-contain" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">LiveOps Made Simple</h3>
              <p className="text-gray-600">Content changes in seconds, not days</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// How It Works Section Component
const HowItWorksSection = () => {
  const steps = [
    {
      number: "01",
      title: "Implement SDK",
      description: "Engineers implement the Basilisk SDK into your server",
      icon: <Download className="h-8 w-8" />,
      details: "Simple integration with your existing tech stack"
    },
    {
      number: "02", 
      title: "Define Data Structure",
      description: "Devs implement data structure in Basilisk (web or code)",
      icon: <Code className="h-8 w-8" />,
      details: "Use our intuitive interface or code directly"
    },
    {
      number: "03",
      title: "Fill Spreadsheets",
      description: "Fill spreadsheets with data & asset routes",
      icon: <FileSpreadsheet className="h-8 w-8" />,
      details: "Google Sheets integration for easy data management"
    },
    {
      number: "04",
      title: "Deploy & Go Live",
      description: "Build spreadsheet to Basilisk → live in minutes!",
      icon: <Zap className="h-8 w-8" />,
      details: "Content updates go live instantly with zero downtime"
    }
  ];

  return (
    <section className="w-full bg-gray-50 py-20">
      <div className="container mx-auto px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-green-100 px-4 py-2 mb-6">
            <Zap className="h-5 w-5 text-green-600" />
            <p className="text-sm font-medium text-green-600">How It Works</p>
          </div>
          <h2 className="mb-6 text-5xl lg:text-6xl text-black font-bold">
            HOW DOES IT WORK?
          </h2>
          <p className="mx-auto max-w-3xl text-xl text-gray-600">
            Get started with Basilisk in just 4 simple steps and transform your LiveOps workflow forever.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-4 mb-16">
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-4 w-24 h-24">
                  <div className="w-24 h-24 rounded-full flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-shadow bg-green-600">
                    {step.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-white border-2 border-green-600 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-green-600">{step.number}</span>
                  </div>
                </div>
                
                <div className="max-w-48">
                  <h3 className="text-lg font-bold text-black mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                  <p className="text-xs text-green-600 font-medium">{step.details}</p>
                </div>
              </div>
              
              {index < steps.length - 1 && (
                <div className="hidden lg:flex items-center justify-center">
                  <ArrowRight className="h-8 w-8 text-green-600" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </section>
  );
};

// Comparison Section Component
const ComparisonSection = () => {
  const comparisonData = [
    { category: "Tool Complexity", without: "Extremely technical tools", with: "Simple as spreadsheets" },
    { category: "Designer Dependencies", without: "Designers depend on engineering", with: "Designers are autonomous" },
    { category: "Creativity", without: "Restricted creativity", with: "Creativity empowered" },
    { category: "Learning Curve", without: "New toolset needed per project", with: "Learn once, use anywhere" },
    { category: "Content Updates", without: "Content changes take days", with: "Content changes take seconds" },
    { category: "Collaboration", without: "Only one dev at a time", with: "Real-time collaboration" },
    { category: "Data Sources", without: "Multiple sources (JSON, CSV, XLSX)", with: "Standardized in Google Sheets" },
    { category: "Documentation", without: "No documentation", with: "Extensive documentation" },
    { category: "Engineering Time", without: "Engineers required for content", with: "Engineers free for new projects" }
  ];

  return (
    <section className="w-full bg-gray-50">
      <div className="container mx-auto px-8">
        <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-lg">
          <div className="text-center mb-12">
            <h3 className="text-4xl lg:text-5xl text-black mb-6 font-bold">
              WITHOUT BASILISK VS WITH BASILISK
            </h3>
            <p className="text-lg text-gray-600">
              See the dramatic difference Basilisk makes in your LiveOps workflow
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b-2 border-gray-300">
                  <th className="text-left py-4 px-4 text-lg font-bold text-gray-800 w-1/4">Category</th>
                  <th className="text-left py-4 px-4 text-lg font-bold text-gray-400 w-3/8">Without Basilisk</th>
                  <th className="text-left py-4 px-4 text-lg font-bold text-green-600 w-3/8">With Basilisk</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-gray-100 transition-colors">
                    <td className="py-4 px-4 font-semibold text-gray-800 align-top">{row.category}</td>
                    <td className="py-4 px-4 text-gray-400 align-top">
                      <div className="flex items-start gap-2">
                        <X className="h-4 w-4 mt-1 flex-shrink-0" />
                        <span>{row.without}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-green-600 align-top">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 mt-1 flex-shrink-0" />
                        <span>{row.with}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

// Book Demo CTA Component
const BookDemoCTA = () => (
  <div className="bg-gradient-to-r from-gray-50 to-gray-100 py-12 px-4">
    <div className="container mx-auto text-center">
      <h3 className="text-2xl font-bold text-gray-900 mb-4">
        Ready to transform your game content management?
      </h3>
      <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
        Schedule a meeting and see how Basilisk can revolutionize your LiveOps content creation process.
      </p>
      <a href="https://calendar.app.google/k8AR5Ya6LqkLWBJL7" target="_blank" rel="noopener noreferrer">
        <Button className="bg-gradient-to-r from-green-600 to-green-400 hover:from-green-500 hover:to-green-300 text-white font-bold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-lg">
          Schedule a Meeting
        </Button>
      </a>
    </div>
  </div>
);

// Footer Component
const Footer = () => (
  <footer className="mt-auto py-8 px-4 border-t border-gray-200 bg-white">
    <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center gap-6">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-green-600 rounded-lg flex items-center justify-center">
            <Zap className="h-5 w-5 text-white" />
          </div>
          <span className="text-xl font-bold text-black">Basilisk</span>
        </div>
        <div className="flex gap-4">
          <a href="https://www.linkedin.com/company/the-liveops-guild/" className="hover:text-green-600 border border-gray-300 rounded-full p-2 flex items-center justify-center transition-colors" target="_blank" rel="noopener noreferrer">
            <LinkedinIcon size={20} className="text-gray-600" />
          </a>
        </div>
      </div>

      <div className="text-sm text-center text-gray-600">
        © Copyright {new Date().getFullYear()} Basilisk. All rights reserved.
      </div>

      <nav className="flex gap-6 text-sm whitespace-nowrap">
        <a href="/privacy/" className="hover:text-green-600 text-gray-600 transition-colors" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        <a href="/terms/" className="hover:text-green-600 text-gray-600 transition-colors" target="_blank" rel="noopener noreferrer">
          Terms & Conditions
        </a>
      </nav>
    </div>
  </footer>
);

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="antialiased">
      <JsonLd 
        type="Organization"
        data={{
          name: "Basilisk",
          url: "https://basilisk.com",
          logo: "https://basilisk.com/bsk-icon-v2.png",
          description: "The new standard for game content management. Cloud-based Content Management Platform for LiveOps content creation that empowers non-technical developers to create and release content of any complexity in seconds.",
          sameAs: ["https://x.com/basilisk", "https://linkedin.com/company/basilisk"]
        }}
      />
      <JsonLd 
        type="WebSite"
        data={{
          name: "Basilisk",
          url: "https://basilisk.com",
          potentialAction: {
            "@type": "SearchAction",
            "target": "https://basilisk.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />

      <div className="flex flex-col min-h-screen">
        <Header />
        <HeroSection />
        <HowItWorksSection />
        <ComparisonSection />
        <BookDemoCTA />
        <Footer />
      </div>
    </div>
  );
};

export default LandingPage;
